// frontend/src/features/livestock/photoInboxSorting.ts
/**
 * What was sorted in the photo inbox this session (plan 4.4): each assign or
 * discard from the assign view, from the moment it is sent. The view moves on
 * to the next photo straight away and the request finishes in the background;
 * this log is what the action bar (pending count, Undo, a failure with Retry)
 * and the grid's "Recently sorted" list show.
 *
 * A module-level store, so it outlives the photo's screen; kept in memory
 * only, the last 20 per tank, and forgotten on sign-out (clear).
 */

import { useMemo, useSyncExternalStore } from 'react';
import type { AssignTarget, PhotoInboxAssignRequest } from '@/types/photoInbox';

/** Kept per tank, besides those still being sent. */
export const SORT_LOG_LIMIT = 20;
/** An assign or discard can be undone this long after the server took it (plan 5.2). */
export const UNDO_WINDOW_MS = 15 * 60 * 1000;

export type SortKind = 'assign' | 'discard';

/**
 * pending: sent, no answer yet. done: the server took it. failed: it didn't
 * (message says why). gone: the photo had been sorted elsewhere. undoing,
 * undone: Undo sent, and taken.
 */
export type SortStatus = 'pending' | 'done' | 'failed' | 'gone' | 'undoing' | 'undone';

export interface NewSortRecord {
  tankId: string;
  photoId: number;
  /** The photo's capture date, to name it ("photo from 3 Aug"). */
  takenAt: string;
  thumbUrl: string | null;
  kind: SortKind;
  /** Assign: where it was sent (an organism unless 'tank'); null or missing for a discard. */
  target?: AssignTarget | null;
  /** Assign: the organism picked; null for a tank shot. */
  livestockId: number | null;
  livestockName: string | null;
  /** Assign: the request, kept for Retry. */
  body: PhotoInboxAssignRequest | null;
  /** Assign: the Recent chips before this assign, put back by Undo. */
  recentsBefore: number[] | null;
}

export interface SortRecord extends NewSortRecord {
  key: number;
  status: SortStatus;
  /**
   * Assign: the organism the server says the photo is on. Another one than
   * picked when the photo had been assigned already (the server answers a
   * repeated assign with the first one).
   */
  resultLivestockId: number | null;
  resultLivestockName: string | null;
  /** Assign: where the server says the photo is. */
  resultTarget: AssignTarget | null;
  /**
   * Assign: the photo had gone to the other target already (409
   * assigned_elsewhere), so nothing was assigned here and there is no Undo.
   */
  elsewhere: boolean;
  /** Why it failed (failed), or how (gone). */
  message: string | null;
  canRetry: boolean;
  /** Why Undo failed, and whether that was final (too late, photo moved on). */
  undoMessage: string | null;
  undoBlocked: boolean;
  /** The user has dealt with a failure (opened the photo, or dismissed it). */
  dismissed: boolean;
  /** When it last changed (ms); notices hide some seconds later. */
  changedAt: number;
  /** When the server took it (ms), for the undo window. */
  doneAt: number | null;
}

type Listener = () => void;

let records: SortRecord[] = [];
let nextKey = 1;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function busy(record: SortRecord): boolean {
  return record.status === 'pending' || record.status === 'undoing';
}

/** Drops a tank's oldest finished records beyond the limit. */
function trim(list: SortRecord[], tankId: string): SortRecord[] {
  let finished = 0;
  const keep = new Set<number>();
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const record = list[index] as SortRecord;
    if (record.tankId !== tankId || busy(record)) continue;
    finished += 1;
    if (finished <= SORT_LOG_LIMIT) keep.add(record.key);
  }
  return list.filter((record) => record.tankId !== tankId || busy(record) || keep.has(record.key));
}

export const sortLog = {
  add(record: NewSortRecord): SortRecord {
    const added: SortRecord = {
      ...record,
      key: nextKey++,
      status: 'pending',
      resultLivestockId: null,
      resultLivestockName: null,
      resultTarget: null,
      elsewhere: false,
      message: null,
      canRetry: false,
      undoMessage: null,
      undoBlocked: false,
      dismissed: false,
      changedAt: Date.now(),
      doneAt: null,
    };
    records = trim([...records, added], record.tankId);
    emit();
    return added;
  },

  update(key: number, patch: Partial<Omit<SortRecord, 'key'>>): void {
    let changed = false;
    records = records.map((record) => {
      if (record.key !== key) return record;
      changed = true;
      return { ...record, ...patch, changedAt: Date.now() };
    });
    if (!changed) return;
    const tankId = records.find((record) => record.key === key)?.tankId;
    if (tankId) records = trim(records, tankId);
    emit();
  },

  get(key: number): SortRecord | undefined {
    return records.find((record) => record.key === key);
  },

  /** A tank's records, newest first. */
  forTank(tankId: string): SortRecord[] {
    return records.filter((record) => record.tankId === tankId).reverse();
  },

  /** The photo's assign or discard still being sent, if any. */
  pendingFor(tankId: string, photoId: number): SortRecord | undefined {
    return records.find(
      (record) =>
        record.tankId === tankId && record.photoId === photoId && record.status === 'pending'
    );
  },

  /** Every record; the same array until something changes (useSyncExternalStore). */
  snapshot(): readonly SortRecord[] {
    return records;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Forgets everything, for sign-out. */
  clear(): void {
    records = [];
    emit();
  },
};

/** A tank shot's name where an organism's would be. */
export const TANK_SHOT_NAME = 'Tank shot';

/** Assign: where the photo went, once the server has answered, else where it was sent. */
export function targetOf(record: SortRecord): AssignTarget {
  return record.resultTarget ?? record.target ?? 'organism';
}

/** Assign: the photo turned out to be somewhere other than picked (it was sorted before). */
export function wasSortedBefore(record: SortRecord): boolean {
  if (record.elsewhere) return true;
  return (
    targetOf(record) === 'organism' &&
    record.resultLivestockId !== null &&
    record.resultLivestockId !== record.livestockId
  );
}

export function canUndo(record: SortRecord, now = Date.now()): boolean {
  return (
    record.status === 'done' &&
    !record.undoBlocked &&
    record.doneAt !== null &&
    now - record.doneAt < UNDO_WINDOW_MS
  );
}

/** The tank's records, newest first, kept current. */
export function useSortRecords(tankId: string | undefined): SortRecord[] {
  const all = useSyncExternalStore(sortLog.subscribe, sortLog.snapshot);
  return useMemo(
    () => (tankId ? all.filter((record) => record.tankId === tankId).reverse() : []),
    [all, tankId]
  );
}
